import type {
  SavedSearchRequest,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import { createSearchConditionName } from '@doda/common/src/logics/saveCondition/saveCondition';
import { opToSaveConditionQuery } from '@doda/common/src/utils/optionQueryUtil';
import { Methods as JobSearchListRequestType } from '@doda/api-client/aspida/api/v1/jobs';
import { createSearchConditionRequestParams } from '../createSearchConditionRequestParams';

/**
 * 検索条件保存APIリクエストデータを生成
 *
 * @param {AppQueryParams} query - 検索クエリパラメータ
 * @returns {SavedSearchRequest} - 検索条件保存APIリクエストデータ
 */
export const createSavedSearchesData = (query: AppQueryParams) => {
  const paramsMaster = createSearchConditionRequestParams(query);
  const requestData: SavedSearchRequest = { name: createSearchConditionName() };

  if (query.ar)
    requestData.areaIdList = paramsMaster.areaId?.map((value) => Number(value));
  if (query.pr)
    requestData.prefectureIdList = paramsMaster.prefectureId?.map((value) =>
      Number(value)
    );
  if (query.ci) requestData.cityIdList = paramsMaster.cityId;
  if (query.wa) requestData.wardIdList = paramsMaster.wardId;
  if (query.rt) requestData.routeIdList = paramsMaster.routeId;
  if (query.st) requestData.stationIdList = paramsMaster.stationId;
  if (query.oc) {
    requestData.searchJobTypeLIdList = paramsMaster.searchJobTypeLId;
    requestData.searchJobTypeMIdList = paramsMaster.searchJobTypeMId;
    requestData.searchJobTypeSIdList = paramsMaster.searchJobTypeSId;
  }
  if (query.ind) {
    requestData.industryLIdList = paramsMaster.industryLId;
    requestData.industrySIdList = paramsMaster.industrySId;
  }
  if (query.k) requestData.includedKeywordList = paramsMaster.includedKeyword;
  if (query.kwc)
    requestData.includedKeywordCategory = paramsMaster.includedKeywordCategory;
  if (query.niwc)
    requestData.excludedKeywordCategory = paramsMaster.excludedKeywordCategory;
  if (query.ni) requestData.excludedKeywordList = paramsMaster.excludedKeyword;
  if (query.op)
    Object.entries(opToSaveConditionQuery(query.op)).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: 型を合わせる
      requestData[key] = value;
    });
  if (query.es) requestData.establishList = paramsMaster.establish;
  if (query.ne) requestData.employNumList = paramsMaster.employNum;
  if (query.ha) {
    requestData.upperLimitIncome = paramsMaster.upperLimitIncome;
    requestData.lowerLimitIncome = paramsMaster.lowerLimitIncome;
  }
  if (query.tp) requestData.jobOfferKind = paramsMaster.jobOfferKind;

  return requestData;
};

/**
 * 検索条件保存レスポンスから検索結果一覧リクエストパラメータ生成
 *
 * @param {Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>} res - 検索条件保存レスポンス
 * @returns {JobSearchListRequestType['get']['query']} - 検索結果一覧リクエストパラメータ
 */
export const convertSaveSearchesToJobSearches = (
  res: Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>
): JobSearchListRequestType['get']['query'] => {
  const IGNORE_LIST = ['no', 'name', 'savedDate'];
  const NO_CONVERT_LIST = [
    'includedKeywordCategory',
    'excludedKeywordCategory',
    'isNewArrival',
    'isNearDeadline',
    'lowerLimitIncome',
    'upperLimitIncome',
    'jobOfferKind',
  ];
  const CHANGE_NUMBER_TO_STRING_LIST = ['areaIdList', 'prefectureIdList'];
  return Object.keys(res)
    .filter(
      (key) =>
        !IGNORE_LIST.includes(key) &&
        !(
          Array.isArray(res[key as keyof typeof res]) &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TODO: 文字列・配列以外にの型を含む値から length を呼び出しているので要修正
          !res[key as keyof typeof res].length
        )
    )
    .reduce((prev, next) => {
      const value = CHANGE_NUMBER_TO_STRING_LIST.includes(next)
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TODO: 配列以外は map 使えないので要修正
          res[next as keyof typeof res].map((val) => String(val))
        : res[next as keyof typeof res];
      return NO_CONVERT_LIST.includes(next)
        ? { ...prev, [next]: value }
        : { ...prev, [`${next.replace('List', '')}`]: value };
    }, {});
};
